import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

/**
 * Setup Wizard Controller
 *
 * Manages the data source setup wizard with:
 * - Field selection (select all, select essential, clear)
 * - Relationship selection with target status tracking
 * - Live count updates
 * - Review summary updates
 * - Search/filter functionality
 */
export default class extends Controller {
  static targets = [
    "step",
    "fieldCheckbox",
    "relationshipCheckbox",
    "relatedFieldCheckbox",
    "fieldsCount",
    "relationshipsCount",
    "relatedFieldsCount",
    "fieldsList",
    "relationshipsList",
    "relatedFieldsContainer",
    "relatedFieldsSection",
    "noRelationshipsPlaceholder",
    "fieldSearch",
    "newTargetsAlert",
    "newTargetsList",
    "reviewSummary",
    "reviewDataSource",
    "reviewDataSourceName",
    "reviewFieldsCount",
    "reviewRelationshipsCount",
    "reviewRelatedFieldsCount",
    "reviewNewTargets",
    "reviewNewTargetsList",
    "reviewNewTargetsListItems",
    "reviewNewTargetsCount",
    "submitButton",
  ];

  static values = {
    creatingNew: Boolean
  };

  connect() {
    this.updateCounts();
  }

  // Field selection methods

  selectAllFields() {
    this.fieldCheckboxTargets.forEach((checkbox) => {
      const row = checkbox.closest("tr");
      if (!row.classList.contains("hidden")) {
        checkbox.checked = true;
      }
    });
    this.updateCounts();
  }

  selectEssentialFields() {
    this.fieldCheckboxTargets.forEach((checkbox) => {
      const row = checkbox.closest("tr");
      checkbox.checked = row.dataset.essential === "true";
    });
    this.updateCounts();
  }

  clearFields() {
    this.fieldCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateCounts();
  }

  filterFields(event) {
    const search = event.target.value.toLowerCase();
    const rows = this.fieldsListTarget.querySelectorAll("tr");

    rows.forEach((row) => {
      const key = row.dataset.fieldKey?.toLowerCase() || "";
      const matches = key.includes(search);
      row.classList.toggle("hidden", !matches);
    });
  }

  // Count updates

  updateCounts() {
    const selectedFields = this.fieldCheckboxTargets.filter((cb) => cb.checked).length;
    const selectedRelationships = this.relationshipCheckboxTargets.filter((cb) => cb.checked).length;
    const selectedRelatedFields = this.hasRelatedFieldCheckboxTarget
      ? this.relatedFieldCheckboxTargets.filter((cb) => cb.checked).length
      : 0;

    // Update step badges
    if (this.hasFieldsCountTarget) {
      this.fieldsCountTarget.textContent = i18n.t('setup_wizard.selected', { count: selectedFields });
    }
    if (this.hasRelationshipsCountTarget) {
      this.relationshipsCountTarget.textContent = i18n.t('setup_wizard.selected', { count: selectedRelationships });
    }
    if (this.hasRelatedFieldsCountTarget) {
      this.relatedFieldsCountTarget.textContent = i18n.t('setup_wizard.selected', { count: selectedRelatedFields });
    }

    // Update related fields visibility based on selected relationships
    this.updateRelatedFieldsVisibility();

    // Update review section
    this.updateReview();
  }

  updateRelatedFieldsVisibility() {
    // Get selected relationship keys
    const selectedRelKeys = this.relationshipCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);

    // Show/hide related fields sections based on selection
    if (this.hasRelatedFieldsSectionTarget) {
      this.relatedFieldsSectionTargets.forEach((section) => {
        const relKey = section.dataset.relationshipKey;
        if (selectedRelKeys.includes(relKey)) {
          section.classList.remove("hidden");
        } else {
          section.classList.add("hidden");
          // Uncheck any related fields from hidden relationships
          const checkboxes = section.querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach((cb) => (cb.checked = false));
        }
      });
    }

    // Show/hide placeholder
    if (this.hasNoRelationshipsPlaceholderTarget) {
      const hasVisibleSections = this.hasRelatedFieldsSectionTarget
        ? this.relatedFieldsSectionTargets.some((s) => !s.classList.contains("hidden"))
        : false;
      this.noRelationshipsPlaceholderTarget.classList.toggle("hidden", hasVisibleSections);
    }
  }

  updateRelatedFieldsCounts() {
    this.updateCounts();
  }

  updateReview() {
    const selectedFields = this.fieldCheckboxTargets.filter((cb) => cb.checked).length;
    const selectedRelationships = this.relationshipCheckboxTargets.filter((cb) => cb.checked).length;
    const selectedRelatedFields = this.hasRelatedFieldCheckboxTarget
      ? this.relatedFieldCheckboxTargets.filter((cb) => cb.checked).length
      : 0;

    // Get new targets that will be created
    const newTargets = this.getNewTargets();

    // Update field count in review
    if (this.hasReviewFieldsCountTarget) {
      this.reviewFieldsCountTarget.textContent = selectedFields;
    }

    // Update relationship count in review
    if (this.hasReviewRelationshipsCountTarget) {
      this.reviewRelationshipsCountTarget.textContent = selectedRelationships;
    }

    // Update related fields count in review
    if (this.hasReviewRelatedFieldsCountTarget) {
      this.reviewRelatedFieldsCountTarget.textContent = selectedRelatedFields;
    }

    // Update data source name in review (for new creation)
    if (this.hasReviewDataSourceTarget && this.creatingNewValue) {
      this.reviewDataSourceTarget.classList.remove("hidden");
      const labelInput = document.querySelector('input[name="label"]');
      if (labelInput && this.hasReviewDataSourceNameTarget) {
        this.reviewDataSourceNameTarget.textContent = labelInput.value || "-";
      }
    } else if (this.hasReviewDataSourceTarget) {
      this.reviewDataSourceTarget.classList.add("hidden");
    }

    // Update new targets alert
    if (this.hasNewTargetsAlertTarget) {
      if (newTargets.length > 0) {
        this.newTargetsAlertTarget.classList.remove("hidden");
        if (this.hasNewTargetsListTarget) {
          this.newTargetsListTarget.textContent = newTargets.join(", ");
        }
      } else {
        this.newTargetsAlertTarget.classList.add("hidden");
      }
    }

    // Update new targets in review
    if (this.hasReviewNewTargetsTarget) {
      if (newTargets.length > 0) {
        this.reviewNewTargetsTarget.classList.remove("hidden");
        if (this.hasReviewNewTargetsListTarget) {
          this.reviewNewTargetsListTarget.classList.remove("hidden");
        }
        if (this.hasReviewNewTargetsCountTarget) {
          this.reviewNewTargetsCountTarget.textContent = newTargets.length;
        }
        if (this.hasReviewNewTargetsListItemsTarget) {
          this.reviewNewTargetsListItemsTarget.innerHTML = newTargets.map((t) => `<li>${t}</li>`).join("");
        }
      } else {
        this.reviewNewTargetsTarget.classList.add("hidden");
        if (this.hasReviewNewTargetsListTarget) {
          this.reviewNewTargetsListTarget.classList.add("hidden");
        }
      }
    }
  }

  getNewTargets() {
    const newTargets = [];
    const seenTargets = new Set();

    this.relationshipCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        const row = checkbox.closest("tr");
        const targetExists = row.dataset.targetExists === "true";
        const targetClass = row.dataset.targetClass;

        if (!targetExists && targetClass && !seenTargets.has(targetClass)) {
          newTargets.push(targetClass);
          seenTargets.add(targetClass);
        }
      }
    });

    return newTargets;
  }

  toggleCreateTargets(event) {
    // This could be used to show/hide warnings about creating targets
    // For now, just update the review
    this.updateReview();
  }
}
